import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { ChevronDown, Plus } from 'lucide-react';
import { API_BASE } from '../helpers';

// Mirror of EMAIL_RE in DMCreateEnvelopeModal.js so the two surfaces agree;
// defined locally to keep the picker self-contained (no cross-import).
const ADD_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * DMVendorPicker — a self-contained searchable dropdown for choosing a
 * CRM contact as an envelope's signer. Hand-built with React state (no new
 * dependency).
 *
 * Contacts are loaded lazily on first open from the existing CRM contacts
 * endpoint (GET /api/contacts has no server-side filtering, so we filter
 * client-side to exclude archived rows) and cached for the component's
 * lifetime. The Azure AD bearer token is attached by the global axios
 * interceptor configured in App.js.
 *
 * A "+ Add new contact" affordance at the bottom of the open panel swaps the
 * list area for a compact four-field form (first/last/email/type). On submit
 * it checks POST /api/contacts/check-duplicate, blocks only on a case-insensitive
 * email match (showing the matches for selection), otherwise POSTs to
 * /api/contacts, appends the new row to the cached list, and selects it.
 *
 * Props:
 *   value    — { contactId, name, email, contactType } | null  (selected contact)
 *   onChange — (contact | null) => void
 */
function DMVendorPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [vendors, setVendors] = useState(null); // null = not loaded yet
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  // ── "Add new contact" affordance state ──────────────────────────────
  // adding: the form/duplicate UI replaces the search + list area.
  // duplicates: null = entry form is shown; array = duplicate-found view.
  const [adding, setAdding] = useState(false);
  const [addForm, setAddForm] = useState({ firstName: '', lastName: '', email: '', contactType: '' });
  const [contactTypes, setContactTypes] = useState(null); // null = not loaded yet
  const [addError, setAddError] = useState(null);
  const [addBusy, setAddBusy] = useState(false);
  const [duplicates, setDuplicates] = useState(null);

  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const firstNameRef = useRef(null);

  // Lazy-load the contact list once, on first open.
  useEffect(() => {
    if (!open || vendors !== null || loading) return;
    setLoading(true);
    setLoadError(null);
    axios.get(`${API_BASE}/contacts`)
      .then(res => {
        const list = (res.data || [])
          .filter(c => !c.IsArchived)
          .map(c => ({
            contactId: c.ContactId,
            name: `${c.FirstName || ''} ${c.LastName || ''}`.trim() || c.CompanyName || 'Unnamed',
            email: c.Email || '',
            company: c.CompanyName || '',
            contactType: c.ContactType || null,
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setVendors(list);
      })
      .catch(() => setLoadError('Could not load contacts. Please try again.'))
      .finally(() => setLoading(false));
  }, [open, vendors, loading]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Focus the search box when the panel opens.
  useEffect(() => {
    if (open && searchRef.current) searchRef.current.focus();
  }, [open]);

  // Lazy-load ContactType options the first time the add form is opened,
  // from the same endpoint the CRM contact form uses.
  useEffect(() => {
    if (!adding || contactTypes !== null) return;
    axios.get(`${API_BASE}/options/ContactType`)
      .then(res => setContactTypes(res.data || []))
      .catch(() => setContactTypes([]));
  }, [adding, contactTypes]);

  // Focus the first field when the entry form appears.
  useEffect(() => {
    if (adding && duplicates === null && firstNameRef.current) firstNameRef.current.focus();
  }, [adding, duplicates]);

  // Closing the panel by any path (selection, outside-click, escape) returns
  // the add affordance to its default list view so the next open is predictable.
  useEffect(() => {
    if (open) return;
    setAdding(false);
    setDuplicates(null);
    setAddError(null);
    setAddBusy(false);
    setAddForm({ firstName: '', lastName: '', email: '', contactType: '' });
  }, [open]);

  const filtered = useMemo(() => {
    const list = vendors || [];
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter(v =>
      v.name.toLowerCase().includes(q) || v.email.toLowerCase().includes(q)
    );
  }, [vendors, search]);

  // Keep the highlighted row in range as the filtered set changes.
  useEffect(() => { setActiveIndex(0); }, [search, vendors]);

  // Single gate for the "+ Add new contact" affordance: it is usable only
  // once the list has loaded successfully. While the initial GET is in flight
  // a late .then would clobber a just-appended row; on the error path the
  // appended row would stay hidden behind the load-error message. Both
  // collapse to: not loading and not errored. (Empty, populated, and
  // filtered-to-zero are all valid loaded states to add from.)
  const addEnabled = !loading && !loadError;

  const selectVendor = (vendor) => {
    onChange(vendor);
    setOpen(false);
    setSearch('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => Math.min(i + 1, Math.max(filtered.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[activeIndex]) selectVendor(filtered[activeIndex]);
    }
  };

  // ── Add-contact handlers ────────────────────────────────────────────
  const openAddForm = () => {
    // Belt-and-suspenders: the button is disabled when !addEnabled, but also
    // short-circuit here so a stale or programmatic invocation can't bypass it.
    if (!addEnabled) return;
    setAddError(null);
    setDuplicates(null);
    setAddForm({ firstName: '', lastName: '', email: '', contactType: '' });
    setAdding(true);
  };

  // Cancel from the form: discard entered values, return to the list (the
  // search term the user had typed is left untouched).
  const cancelAddForm = () => {
    setAdding(false);
    setDuplicates(null);
    setAddError(null);
    setAddBusy(false);
    setAddForm({ firstName: '', lastName: '', email: '', contactType: '' });
  };

  // Back from the duplicate-found view: return to the form with values intact.
  const backFromDuplicates = () => {
    setDuplicates(null);
    setAddError(null);
  };

  const updateAddField = (field, val) => setAddForm(f => ({ ...f, [field]: val }));

  // Select a duplicate candidate. The check-duplicate response carries no
  // ContactType, so to fire onChange with the same shape as a list pick
  // (incl. contactType, which drives the closed-trigger badge) we resolve the
  // already-cached entry by id here. The duplicate list itself is rendered
  // straight from the API response — no cache lookup for that.
  const selectDuplicate = (dup) => {
    const cached = (vendors || []).find(v => v.contactId === dup.ContactId);
    selectVendor(cached || {
      contactId: dup.ContactId,
      name: `${dup.FirstName || ''} ${dup.LastName || ''}`.trim() || dup.Email || 'Unnamed',
      email: dup.Email || '',
      contactType: null,
    });
  };

  const submitAddForm = async () => {
    const firstName = addForm.firstName.trim();
    const lastName = addForm.lastName.trim();
    const email = addForm.email.trim();
    if (!firstName || !lastName || !email) {
      setAddError('First name, last name, and email are required.');
      return;
    }
    if (!ADD_EMAIL_RE.test(email)) {
      setAddError('Please enter a valid email.');
      return;
    }
    setAddError(null);
    setAddBusy(true);

    let dupRes;
    try {
      dupRes = await axios.post(`${API_BASE}/contacts/check-duplicate`, { firstName, lastName, email });
    } catch {
      setAddBusy(false);
      setAddError('Could not check for duplicates. Please try again.');
      return;
    }

    // Only an exact (case-insensitive) email match blocks creation; the
    // endpoint also returns name-only matches, which we ignore.
    const emailMatches = (dupRes.data?.duplicates || []).filter(
      d => d.Email && d.Email.toLowerCase() === email.toLowerCase()
    );
    if (emailMatches.length > 0) {
      setAddBusy(false);
      setDuplicates(emailMatches);
      return;
    }

    const contactType = addForm.contactType || null;
    try {
      const res = await axios.post(`${API_BASE}/contacts`, {
        firstName,
        lastName,
        email,
        contactType: addForm.contactType || undefined,
      });
      const newId = res.data.contactId;
      const name = `${firstName} ${lastName}`.trim();
      // Append to the lifetime cache and re-sort by name instead of refetching,
      // so the new row appears in alphabetical position on the next open.
      setVendors(prev =>
        [...(prev || []), { contactId: newId, name, email, company: '', contactType }]
          .sort((a, b) => a.name.localeCompare(b.name))
      );
      setAddBusy(false);
      selectVendor({ contactId: newId, name, email, contactType });
    } catch {
      setAddBusy(false);
      setAddError('Could not add the contact. Please try again.');
    }
  };

  // Escape inside the add UI stays in the picker: from the form it cancels
  // back to the list; from the duplicate view it returns to the form.
  const handleFormKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      if (duplicates === null) cancelAddForm();
      else backFromDuplicates();
    }
  };

  return (
    <div className="dm-vpicker" ref={rootRef}>
      <button
        type="button"
        className={`dm-vpicker__control ${open ? 'dm-vpicker__control--open' : ''}`}
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span
          className={`dm-vpicker__value ${value ? '' : 'dm-vpicker__value--placeholder'}`}
          style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
        >
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
            {value ? `${value.name}${value.email ? ` (${value.email})` : ''}` : 'Select a contact...'}
          </span>
          {value && value.contactType && (
            <span className="crm-badge crm-badge--taupe" style={{ flexShrink: 0 }}>{value.contactType}</span>
          )}
        </span>
        <ChevronDown size={16} />
      </button>

      {open && (
        <div className="dm-vpicker__panel">
          {!adding && (
            <>
              <input
                ref={searchRef}
                type="text"
                className="dm-vpicker__search"
                placeholder="Search contacts by name or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              <div className="dm-vpicker__list" role="listbox">
                {loading && <div className="dm-vpicker__empty">Loading contacts...</div>}
                {loadError && !loading && <div className="dm-vpicker__empty">{loadError}</div>}
                {!loading && !loadError && filtered.length === 0 && (
                  <div className="dm-vpicker__empty">No contacts found</div>
                )}
                {!loading && !loadError && filtered.map((v, i) => (
                  <button
                    type="button"
                    key={v.contactId}
                    className={`dm-vpicker__option ${i === activeIndex ? 'dm-vpicker__option--active' : ''}`}
                    onMouseEnter={() => setActiveIndex(i)}
                    onClick={() => selectVendor(v)}
                    role="option"
                    aria-selected={value?.contactId === v.contactId}
                  >
                    <span className="dm-vpicker__option-name">
                      {v.name}{v.company ? ` — ${v.company}` : ''}
                    </span>
                    {v.contactType && (
                      <span className="crm-badge crm-badge--taupe" style={{ margin: '0 6px' }}>{v.contactType}</span>
                    )}
                    <span className="dm-vpicker__option-email">{v.email || 'No email on file'}</span>
                  </button>
                ))}
              </div>
              <button
                type="button"
                className="dm-vpicker__option"
                onClick={openAddForm}
                disabled={!addEnabled}
                title={addEnabled ? undefined : (loadError ? 'Contacts failed to load. Try again.' : 'Loading contacts...')}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', borderTop: '1px solid var(--border-light)', color: addEnabled ? 'var(--dm-gold-dark)' : 'var(--text-muted)', fontWeight: 600, cursor: addEnabled ? undefined : 'not-allowed' }}
              >
                <Plus size={16} /> Add new contact
              </button>
            </>
          )}

          {adding && duplicates === null && (
            <div onKeyDown={handleFormKeyDown} style={{ padding: '0.75rem' }}>
              <div className="crm-form__group">
                <label className="crm-form__label">First name *</label>
                <input
                  ref={firstNameRef}
                  type="text"
                  className="crm-form__input"
                  value={addForm.firstName}
                  onChange={(e) => updateAddField('firstName', e.target.value)}
                />
              </div>
              <div className="crm-form__group">
                <label className="crm-form__label">Last name *</label>
                <input
                  type="text"
                  className="crm-form__input"
                  value={addForm.lastName}
                  onChange={(e) => updateAddField('lastName', e.target.value)}
                />
              </div>
              <div className="crm-form__group">
                <label className="crm-form__label">Email *</label>
                <input
                  type="email"
                  className="crm-form__input"
                  value={addForm.email}
                  onChange={(e) => updateAddField('email', e.target.value)}
                />
              </div>
              <div className="crm-form__group">
                <label className="crm-form__label">Contact type</label>
                <select
                  className="crm-form__select"
                  value={addForm.contactType}
                  onChange={(e) => updateAddField('contactType', e.target.value)}
                >
                  <option value="">Select...</option>
                  {(contactTypes || []).map(o => (
                    <option key={o.OptionId} value={o.Value}>{o.Label}</option>
                  ))}
                </select>
              </div>
              {addError && <div className="dm-vpicker__empty">{addError}</div>}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                <button type="button" className="crm-btn crm-btn--secondary" onClick={cancelAddForm} disabled={addBusy}>
                  Cancel
                </button>
                <button type="button" className="crm-btn dm-btn--gold" onClick={submitAddForm} disabled={addBusy}>
                  {addBusy && <span className="dm-inline-spinner" />}
                  Add Contact
                </button>
              </div>
            </div>
          )}

          {adding && duplicates !== null && (
            <div onKeyDown={handleFormKeyDown} style={{ padding: '0.75rem' }}>
              <div style={{ fontSize: '0.813rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
                A contact with this email already exists. Select it below, or go back to edit.
              </div>
              <div role="listbox">
                {duplicates.map(d => (
                  <button
                    type="button"
                    key={d.ContactId}
                    className="dm-vpicker__option"
                    onClick={() => selectDuplicate(d)}
                    role="option"
                    aria-selected={false}
                  >
                    <span className="dm-vpicker__option-name">
                      {`${d.FirstName || ''} ${d.LastName || ''}`.trim() || 'Unnamed'}
                    </span>
                    <span className="dm-vpicker__option-email">{d.Email || 'No email on file'}</span>
                  </button>
                ))}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                <button type="button" className="crm-btn crm-btn--secondary" onClick={backFromDuplicates}>
                  Back
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default DMVendorPicker;
